import type { EducatorCohortDetailsResponse } from '@mc/api'
import { useCohortDetails } from '../api-hooks/enrollment/useCohortDetails'
import { deriveHeroStrip } from '../utils/deriveHeroStrip'
import type { HeroStripConfig } from '../utils/deriveHeroStrip'
import { resolveEffectiveWindowType } from '../utils/resolveEnrollmentWindow'
import type { EducatorEnrollmentWindowType } from '../utils/resolveEnrollmentWindow'

export type EnrollmentWindow = NonNullable<
  EducatorCohortDetailsResponse['currentWindow']
>

/** Active window's `lpBanner` messages, or null when absent/empty. */
export const getWindowLpBannerMessages = (
  cohortDetails: EducatorCohortDetailsResponse | undefined,
): string[] | null => {
  const messages = cohortDetails?.currentWindow?.text?.messages?.lpBanner
  return messages && messages.length > 0 ? messages : null
}

export interface EnrollmentBannerState {
  /** Active enrollment window (from `current_window`), or null. */
  enrollmentWindow: EnrollmentWindow | null
  /** Active window's `lpBanner` messages. The banner renders only when set. */
  windowLpBannerMessages: string[] | null
  /** Render the enrollment-window banner. */
  showWindowBanner: boolean
  /**
   * The effective enrollment window driving hero + CTA (see
   * `resolveEffectiveWindowType`). Undefined when no window resolves.
   */
  windowType: EducatorEnrollmentWindowType | undefined
  /** Resolved hero-strip config for the three landing surfaces. */
  heroStrip: HeroStripConfig
  /**
   * @deprecated Derived from `windowType === 'closed'` (which now includes the
   * `state === 'closed'` floor). Pages read `heroStrip` for the hero; kept for
   * non-hero consumers only.
   */
  isEnrollmentClosed: boolean
  /** Pre-formatted "June 1"-style application deadline, when available. */
  applicationDeadline: string | undefined
  /** Pre-formatted "July 7, 2026"-style program start date, when available. */
  programStartDate: string | undefined
  /** Name of the cohort driving the landing page (e.g. "Winter '26"). */
  currentCohortName: string | undefined
  /** Next-cohort label (e.g. "Fall '26"), surfaced during the closed phase. */
  nextCohortName: string | undefined
}

/**
 * Resolves the enrollment-window banner for landing-style surfaces. The banner
 * renders only when the active window has `lpBanner` copy — there is no
 * deadline/BAU fallback.
 */
export const useEnrollmentBanner = (
  /**
   * SSR-prefetched cohort-details, seeded into the shared `useCohortDetails`
   * cache (no first-paint flash). Read before the CTA hook, so it also seeds
   * `useEnrollmentCtaConfig`.
   */
  initialCohortDetails?: EducatorCohortDetailsResponse,
): EnrollmentBannerState => {
  const { data: cohortDetails } = useCohortDetails({
    initialData: initialCohortDetails,
  })
  const enrollmentWindow = cohortDetails?.currentWindow ?? null
  const windowLpBannerMessages = getWindowLpBannerMessages(cohortDetails)
  const showWindowBanner = !!windowLpBannerMessages

  const applicationDeadline = cohortDetails?.applicationDeadline ?? undefined
  const programStartDate = cohortDetails?.programStartDate ?? undefined
  const currentCohortName = cohortDetails?.currentCohortName ?? undefined
  const nextCohortName = cohortDetails?.nextCohortName ?? undefined

  const windowType = resolveEffectiveWindowType({
    windowType: enrollmentWindow?.windowType,
    state: cohortDetails?.state,
  })
  const heroStrip = deriveHeroStrip({
    windowType,
    currentWindowEndsAt: enrollmentWindow?.endsAt,
    enrollmentWindows: cohortDetails?.enrollmentWindows,
    applicationDeadline,
    programStartDate,
    nextCohortName,
  })

  return {
    enrollmentWindow,
    windowLpBannerMessages,
    showWindowBanner,
    windowType,
    heroStrip,
    isEnrollmentClosed: windowType === 'closed',
    applicationDeadline,
    programStartDate,
    currentCohortName,
    nextCohortName,
  }
}
